import type { Moment } from 'moment'

import { FlightsFareTypes } from 'constants/flight'
import type { SelectingDateType } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'

/**
 * What the date menu should do once a day has been taken.
 *
 * `keep` leaves it exactly as it was — a half-finished return trip stays open
 * on the same field rather than re-opening itself.
 */
export type FlightDateMenuOutcome =
  | { action: 'close' }
  | { action: 'keep' }
  | { action: 'open', selecting: SelectingDateType }

/**
 * What a click on a day should do to the flight search.
 *
 * Deliberately a description rather than a dispatch: this decides the fare a
 * traveller is quoted, so it is kept pure and tested directly instead of being
 * spread through a component's click handler.
 */
export interface FlightDateSelectionOutcome {
  /** Absent when the departure is unchanged, or has no quote worth re-sending. */
  checkIn?: { date: Moment, quotedPrice?: number }
  /** `date: null` clears the return leg. */
  checkOut?: { date: Moment | null, quotedPrice?: number | null }
  menu: FlightDateMenuOutcome
  /** Which leg the traveller just chose, for analytics parity. */
  chose: 'departure' | 'arrival'
}

export interface FlightDateSelectionInput {
  fareType?: FlightsFareTypes
  /** Which field the traveller opened the calendar from, when they used one. */
  selectingDateType?: SelectingDateType
  checkinDate?: Moment
  checkoutDate?: Moment
  pickedDate: Moment
  /**
   * Cached return fare for the picked day, in whole currency units. The search
   * quotes half of it, because the cached figure covers both legs.
   */
  pickedDayCachedPrice?: number
  /** The same, for the day the departure already sits on. */
  checkinDayCachedPrice?: number
}

/**
 * The search quotes per leg, and the cached fare covers the return trip.
 *
 * A day the fare cache does not carry has no quote rather than a free one.
 * The quote records the fare the calendar actually showed, so analytics can
 * compare it against what the search went on to find; a day showing no fare
 * that reports `0` claims the traveller was quoted nothing to pay.
 */
function toQuotedPrice(cachedPrice: number | undefined): number | undefined {
  return cachedPrice === undefined ? undefined : Math.floor(cachedPrice / 2)
}

/**
 * Mirrors how the standard flight calendar resolves a day click.
 *
 * One-way sets the departure and closes. A return trip follows the field the
 * traveller opened it from: "Departing" takes the day and moves them on to the
 * return, "Returning" completes the trip. With neither field in play it fills
 * whichever end is still open, and otherwise starts the trip again from the
 * day just picked.
 */
export function resolveFlightDateSelection(input: FlightDateSelectionInput): FlightDateSelectionOutcome {
  const {
    fareType,
    selectingDateType,
    checkinDate,
    checkoutDate,
    pickedDate,
    pickedDayCachedPrice,
    checkinDayCachedPrice,
  } = input

  const pickedPrice = toQuotedPrice(pickedDayCachedPrice)

  if (fareType === FlightsFareTypes.ONE_WAY) {
    return {
      checkIn: { date: pickedDate, quotedPrice: pickedPrice },
      menu: { action: 'close' },
      chose: 'departure',
    }
  }

  if (selectingDateType === 'returning' && checkinDate && pickedDate.isSameOrAfter(checkinDate)) {
    return {
      checkOut: { date: pickedDate, quotedPrice: pickedPrice },
      menu: { action: 'close' },
      chose: 'arrival',
    }
  }

  if (selectingDateType === 'departing') {
    return {
      checkIn: { date: pickedDate, quotedPrice: pickedPrice },
      // A return that now sits before the new departure is no longer a trip.
      checkOut: checkoutDate && pickedDate.isAfter(checkoutDate)
        ? { date: null, quotedPrice: null }
        : undefined,
      menu: { action: 'open', selecting: 'returning' },
      chose: 'departure',
    }
  }

  if (checkinDate && !checkoutDate && pickedDate.isSameOrAfter(checkinDate)) {
    const checkinPrice = toQuotedPrice(checkinDayCachedPrice)
    return {
      /*
       * The departure is re-sent so its own quote is filled in alongside the
       * return that completes the trip — but only while the fare cache still
       * covers its day. Fares are cached per visible month window, so a
       * traveller who scrolled months on to find their return has long since
       * dropped the departure out of it, and re-sending then would overwrite
       * the quote taken when they picked that day with nothing at all.
       */
      checkIn: checkinPrice === undefined ? undefined : { date: checkinDate, quotedPrice: checkinPrice },
      checkOut: { date: pickedDate, quotedPrice: pickedPrice },
      menu: { action: 'close' },
      chose: 'arrival',
    }
  }

  return {
    checkIn: { date: pickedDate, quotedPrice: pickedPrice },
    checkOut: { date: null, quotedPrice: null },
    // Left open: the traveller still needs to pick a return.
    menu: { action: 'keep' },
    chose: 'departure',
  }
}
